<template>
  <tr class="hover:bg-gray-50 text-left">
    <td class="px-4 py-2 text-gray-900 text-sm font-medium leading-5 border-b">
      <div class="flex flex-row gap-2">
        <div class="flex flex-col shrink-0">
          <img
            v-if="hasProfilePicture()"
            :src="getProfilePicture()"
            alt="Profile Picture"
            class="w-10 h-10 rounded-full overflow-hidden"
          />
          <div
            v-else
            v-html="BLANK_PROFILE_PICTURE_ICON"
            class="w-10 h-10 rounded-full overflow-hidden"
          ></div>
        </div>
        <div class="flex flex-col">
          <a
            v-if="row.user.slug && programSlug"
            :href="getExpertCalendarUrl(row.user.slug)"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {{ row.user.firstName + " " + row.user.lastName }}
          </a>
          <span v-else>{{ row.user.firstName + " " + row.user.lastName }}</span>
          <div
            v-if="row.user.title || row.user.companyName"
            class="text-xs text-gray-800 mt-1"
          >
            <span v-if="row.user.title">{{ row.user.title }}</span>
            <span v-if="row.user.title && row.user.companyName"> at </span>
            <span v-if="row.user.companyName">{{ row.user.companyName }}</span>
          </div>
        </div>
      </div>
    </td>
    <td class="px-4 py-2 text-gray-900 text-sm font-medium leading-5 border-b">
      <div class="flex flex-col">
        <span>{{ row.user.email }}</span>
      </div>
    </td>
    <td class="px-4 py-2 font-medium text-xs leading-4 border-b">--</td>
    <td class="px-4 py-2 font-medium text-xs leading-4 border-b">
      <div class="relative inline-block group">
        <span class="px-2 rounded-lg text-gray-800 bg-orange-100">Applied</span>
        <!-- Tooltip -->
        <div
          class="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-max max-w-xs px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none"
        >
          Signed up via email invite, landing page invite, or software search
          and then applied to join your program.
        </div>
      </div>
    </td>
    <td class="px-4 py-2 font-medium text-xs leading-4 border-b">
      Applied on {{ convertToDate(row.createdAt) }}
    </td>
    <td class="flex justify-center px-4 py-2 border-b">
      <BaseButton
        type="button"
        class="flex justify-center text-xs items-center py-2 px-4 rounded-md bg-indigo-100 text-primary-purple"
        style="width: auto"
        @click="$emit('review', rowIndex)"
      >
        Review
      </BaseButton>
    </td>
  </tr>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { RequestRow } from "@/types/dashboard-types";
import BaseButton from "@/components/shared/BaseButton.vue";
import { useUserStore } from "@/stores/user";
import { BLANK_PROFILE_PICTURE_ICON } from "@/assets/svg/shared/svg-constants";

export default defineComponent({
  name: "AppliedRequestRow",
  components: {
    BaseButton,
  },
  props: {
    row: {
      type: Object as PropType<RequestRow>,
      required: true,
    },
    rowIndex: {
      type: Number,
      required: true,
    },
    convertToDate: {
      type: Function,
      required: true,
    },
  },
  emits: ["review"],
  setup(props) {
    const userStore = useUserStore();

    const programSlug = computed(() => {
      const program = userStore.selectedProgram;
      if (!program) return null;
      return program.slug;
    });

    const getExpertCalendarUrl = (expertSlug: string) => {
      if (!expertSlug || !programSlug.value) return "#";
      return `/${programSlug.value}/${expertSlug}`;
    };

    const hasProfilePicture = (): boolean => {
      return !!props.row.user.profile_picture;
    };

    const getProfilePicture = (): string => {
      if (!hasProfilePicture()) return "";
      return props.row.user.profile_picture || "";
    };

    return {
      getExpertCalendarUrl,
      programSlug,
      hasProfilePicture,
      getProfilePicture,
      BLANK_PROFILE_PICTURE_ICON,
    };
  },
});
</script>
